// A workspace's rows of my own things (Cases, Agents, Documents, People) open
// Me's pages with the workspace still around them: its sidebar, its tile
// current on the rail, Me narrowed to its label (the person, 2026-10-30).
// Which workspace that is, per window.
//
// Promise:
// - Showing a workspace's page makes it the one; so a link from there into
//   Me's pages (a row, a Case's chip, an Inbox row) stays in the workspace.
// - A page of Me under one of those rows keeps it; any other page of Me
//   (the Inbox, DM, the settings) and a page of neither place drop it.
// - Pressing Me on the rail drops it before going there.
// - Not kept across reloads (as Me's label is not).
// - At a phone's width there is no sidebar: Me's pages are Me's there.
import { useRouter, useRouterState } from "@tanstack/react-router";
import { usePhone } from "@trameria/ui-core";
import { useEffect, useSyncExternalStore } from "react";
import { slugOfPath } from "./places";

/** Me's pages a workspace's sidebar has rows for. */
export const MY_THINGS_PATHS = [
  "/me/cases",
  "/me/agents",
  "/me/documents",
  "/me/people",
] as const;

const underMyThings = (path: string) =>
  MY_THINGS_PATHS.some((p) => path === p || path.startsWith(`${p}/`));

let held: string | null = null;
const subs = new Set<() => void>();

function hold(slug: string | null) {
  if (slug === held) return;
  held = slug;
  for (const fn of subs) fn();
}

/** Follows the page about to be shown. */
function follow(path: string): void {
  const slug = slugOfPath(path);
  if (slug) hold(slug);
  else if (!underMyThings(path)) hold(null);
}

/**
 * Follows the pages of this window (the workspace part's `Background`):
 * before each is drawn, so its frame is right from the first paint.
 */
export function MePlaceFollower(): null {
  const router = useRouter();
  useEffect(() => {
    follow(router.state.location.pathname);
    return router.subscribe("onBeforeNavigate", ({ toLocation }) =>
      follow(toLocation.pathname),
    );
  }, [router]);
  return null;
}

/** Me pressed on the rail: Me's pages are Me's again. */
export const dropMePlace = () => hold(null);

/**
 * The workspace a page of Me is shown in (its slug), or null when it is
 * shown as Me's. Read outside React (keeping a place's page).
 */
export function mePlaceOf(path: string): string | null {
  return underMyThings(path) ? held : null;
}

/** `mePlaceOf` for the page shown now, as a hook (null at a phone's width). */
export function useMePlace(): string | null {
  const slug = useSyncExternalStore(
    (fn) => {
      subs.add(fn);
      return () => subs.delete(fn);
    },
    () => held,
  );
  const mine = useRouterState({
    select: (s) => underMyThings(s.location.pathname),
  });
  return !usePhone() && mine ? slug : null;
}
